import classNames from 'classnames';
import {
  type CSSProperties,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useResizeObserver } from '@revelio/core';

import { ReplotChartNames } from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import { getFormatter, useChartTheme } from '../../utilities';
import { getOtherColor, getOtherColorDark } from '../../utilities/plot-colors';
import { plotTestId } from '../../utilities/plotTestId';
import { getTextWidth, truncateText } from '../../utilities/truncate-text';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import { getCursorPosition } from '../plot-tooltip/cursor-tooltip';
import {
  PlotTooltip,
  type TooltipPosition,
} from '../plot-tooltip/plot-tooltip';
import styles from './timeline-chart.module.css';
import { exitLabel } from './timeline-events';
import {
  layoutTimeline,
  TIMELINE_OTHER_ID,
  type TimelineFamily,
  type TimelineFoldedCompany,
  type TimelineTick,
  type TimelineTrack,
} from './timeline-layout';
import { TimelineTooltipContent } from './timeline-tooltip';
import type { TimelineData, TimelineHighlight } from './types';

export type TimelineChartProps = {
  data: TimelineData | null;
  highlight?: TimelineHighlight | null;
  color?: string;
  isDrillable?: (id: string) => boolean;
  onDrill?: (
    id: string,
    memberIds: string[],
    color: string | undefined
  ) => void;
} & CommonPlotProps;

const PADDING_TOP = 0;
const PADDING_BOTTOM = 26;
const PADDING_RIGHT = 12;
const PADDING_LEFT = 12;
const LEAD_IN_WIDTH = 150;
const NAME_COLUMN_WIDTH = 170;
const NAME_COLUMN_MIN_WIDTH = 560;
const NAME_GAP = 8;
const FAMILY_NAME_FONT_SIZE = 12;
const LANE_GAP_ROWS = 0.2;
const LANE_PAD_ROWS = 0.3;
const AXIS_FONT_SIZE = 11;
const TRACK_LABEL_FONT_SIZE = 11;
const MAX_LANES = 8;
const ROW_PITCH = 18;
const LABEL_FONT_RATIO = 0.7;
const LABEL_MIN_FONT_SIZE = 7;
const LABEL_MIN_SHARE = 0.005;
const LABEL_EVERY_ROW_PITCH = 8;
const LABEL_CLEARANCE = 4;
const OBSTACLE_PADDING = 2;
const LABEL_SHIFT_STEPS = 6;
const LABEL_MIN_CHARACTERS = 3;
const DECADE_GRID_YEARS = 10;
const STATION_GAP = 1;
const STATION_HIT_SLOP = 2;
const STATION_MIN_RADIUS = 2.5;
const STATION_MAX_RADIUS = 7;
const EXIT_LABEL_FONT_SIZE = 10;
const EXIT_BAR_HALF = 4.5;
/** The exit link's run along the company's row, before the repeated name. */
const EXIT_LINK_STUB = 20;
const EXIT_LINK_LABEL_GAP = 3;

type Box = { x0: number; x1: number; y0: number; y1: number };

type Lane = TimelineFamily & { fill: string };

type Station = {
  x: number;
  y: number;
  companies: TimelineFoldedCompany[];
  left: boolean;
};

type ForegroundGroup = {
  lane: Lane;
  stations: Station[];
  spans: { track: TimelineTrack; left: boolean }[];
};

type StationPoint = {
  company: TimelineFoldedCompany;
  x: number;
  y: number;
};

type LabelPlacement = {
  track: TimelineTrack;
  text: string;
  x: number;
  y: number;
  fontSize: number;
};

type ExitPlacement = LabelPlacement & {
  /** How it left, after the repeated name; null when nothing fits or applies. */
  outcome: string | null;
};

type ExitLink = NonNullable<TimelineTrack['exitLink']>;

const radiusForCount = (count: number, busiest: number): number =>
  busiest > 1
    ? STATION_MIN_RADIUS +
      (STATION_MAX_RADIUS - STATION_MIN_RADIUS) *
        Math.min((Math.sqrt(count) - 1) / (Math.sqrt(busiest) - 1), 1)
    : STATION_MIN_RADIUS;

const radiusOf = (station: Station, busiest: number): number =>
  radiusForCount(station.companies.length, busiest);

const toStations = (
  points: StationPoint[],
  busiest: number,
  left = false
): Station[] => {
  const byRow = [...points].sort((a, b) => a.y - b.y || a.x - b.x);
  const list: Station[] = [];
  for (const point of byRow) {
    list.push({
      x: point.x,
      y: point.y,
      companies: [point.company],
      left,
    });
    while (list.length > 1) {
      const previous = list[list.length - 2];
      const last = list[list.length - 1];
      if (
        !previous ||
        !last ||
        previous.y !== last.y ||
        last.x - previous.x >=
          radiusOf(previous, busiest) + radiusOf(last, busiest) + STATION_GAP
      ) {
        break;
      }
      const count = previous.companies.length + last.companies.length;
      previous.x =
        (previous.x * previous.companies.length +
          last.x * last.companies.length) /
        count;
      previous.companies.push(...last.companies);
      list.pop();
    }
  }
  return list;
};

const busiestOf = (lists: Station[][]): number =>
  Math.max(1, ...lists.flat().map((station) => station.companies.length));

const atJoin = (company: TimelineFoldedCompany): StationPoint => ({
  company,
  x: company.x,
  y: company.y,
});

const onOwnRow = (company: TimelineFoldedCompany): StationPoint => ({
  company,
  x: company.x,
  y: company.ownY,
});

const atExit = (company: TimelineFoldedCompany): StationPoint[] =>
  company.leftX === null || company.leftY === null
    ? []
    : [{ company, x: company.leftX, y: company.leftY }];

const stationsOverlap = (a: Station, b: Station, busiest: number): boolean =>
  Math.hypot(a.x - b.x, a.y - b.y) <
  radiusOf(a, busiest) + radiusOf(b, busiest) + STATION_GAP;

const overlaps = (a: Box, b: Box): boolean =>
  a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;

const exitLinkPath = (link: ExitLink): string =>
  `M${link.x},${link.y1}V${link.y0}H${link.x + EXIT_LINK_STUB}`;

const exitLinkBoxes = (link: ExitLink): Box[] => [
  {
    x0: link.x - OBSTACLE_PADDING,
    x1: link.x + OBSTACLE_PADDING,
    y0: Math.min(link.y0, link.y1),
    y1: Math.max(link.y0, link.y1),
  },
  {
    x0: link.x,
    x1: link.x + EXIT_LINK_STUB,
    y0: link.y0 - OBSTACLE_PADDING,
    y1: link.y0 + OBSTACLE_PADDING,
  },
];

const measureText = (text: string, fontSize: number): number =>
  getTextWidth({ text, fontSize }) ?? text.length * fontSize * 0.5;

const isReadable = (text: string, full: string): boolean =>
  text.replace(/[….]+$/, '').length >=
  Math.min(LABEL_MIN_CHARACTERS, full.length);

const labelBox = ({ text, x, y, fontSize }: LabelPlacement): Box => ({
  x0: x,
  x1: x + measureText(text, fontSize),
  y0: y + 1.5 - fontSize,
  y1: y + 1.5,
});

const laneVars = (lane: Lane): CSSProperties =>
  ({ '--replots-timeline-line': lane.fill }) as CSSProperties;

const format = getFormatter(FormatType.INTEGER);

export const TimelineChart = ({
  data,
  highlight = null,
  color,
  isDrillable,
  onDrill,
  loading = false,
  className,
}: TimelineChartProps) => {
  const { theme, breakdownPalette } = useChartTheme();
  const otherColor = theme === 'dark' ? getOtherColorDark() : getOtherColor();
  const containerRef = useRef<HTMLDivElement>(null);
  const { width, height } = useResizeObserver(containerRef);
  const nameColumnWidth =
    width >= NAME_COLUMN_MIN_WIDTH ? NAME_COLUMN_WIDTH : 0;

  const plotRef = useRef<SVGSVGElement>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoveredStation, setHoveredStation] = useState<Station | null>(null);
  const [cursor, setCursor] = useState<TooltipPosition | null>(null);
  const [pointerX, setPointerX] = useState<number | null>(null);
  const trackCursor = useCallback(
    (event: { clientX: number; clientY: number }) => {
      setCursor(getCursorPosition(event));
      const bounds = plotRef.current?.getBoundingClientRect();
      if (bounds) setPointerX(event.clientX - bounds.left);
    },
    []
  );
  const hasData = !!data && data.entities.length > 0;

  const layout = useMemo(
    () =>
      layoutTimeline({
        data,
        width,
        height,
        paddingLeft: PADDING_LEFT,
        paddingRight: PADDING_RIGHT + nameColumnWidth,
        paddingTop: PADDING_TOP,
        paddingBottom: PADDING_BOTTOM,
        leadIn: LEAD_IN_WIDTH,
        familyGapRows: LANE_GAP_ROWS,
        familyPadRows: LANE_PAD_ROWS,
        fitRows: { maxFamilies: MAX_LANES, pitch: ROW_PITCH },
      }),
    [data, width, height, nameColumnWidth]
  );
  const {
    isPlotSizeValid,
    families,
    tracks,
    ticks,
    plotLeft,
    plotRight,
    plotTop,
    plotBottom,
    domain,
    pitch,
  } = layout;

  const lanes = useMemo(
    (): Lane[] =>
      families.map((family, index) => {
        const paletteFill =
          family.id === TIMELINE_OTHER_ID
            ? otherColor
            : (breakdownPalette[index % Math.max(breakdownPalette.length, 1)] ??
              otherColor);
        return { ...family, fill: color ?? paletteFill };
      }),
    [families, breakdownPalette, otherColor, color]
  );
  const laneById = useMemo(
    () => new Map(lanes.map((lane) => [lane.id, lane])),
    [lanes]
  );
  const laneIdOf = useMemo(
    () => new Map(tracks.map((track) => [track.id, track.familyId])),
    [tracks]
  );
  const interactive = useCallback(
    (id: string, elementClass: string | undefined) => {
      const memberIds = laneById.get(id)?.memberIds ?? [];
      const laneId = laneById.has(id) ? id : laneIdOf.get(id);
      const laneFill =
        laneId === undefined || laneId === TIMELINE_OTHER_ID
          ? undefined
          : laneById.get(laneId)?.fill;
      const drillable =
        onDrill !== undefined &&
        (memberIds.length > 0 || (isDrillable?.(id) ?? false));
      return {
        className: classNames(elementClass, drillable && styles.drillable),
        onMouseEnter: () => setHoveredId(id),
        onMouseMove: trackCursor,
        onMouseLeave: () => {
          setHoveredId(null);
          setHoveredStation(null);
          setCursor(null);
        },
        ...(drillable && {
          onClick: () => {
            setHoveredId(null);
            setHoveredStation(null);
            setCursor(null);
            onDrill?.(id, memberIds, laneFill);
          },
        }),
      };
    },
    [trackCursor, isDrillable, onDrill, laneById, laneIdOf]
  );
  const tracksByLane = useMemo(() => {
    const byLane = new Map<string, TimelineTrack[]>();
    for (const track of tracks) {
      const list = byLane.get(track.familyId) ?? [];
      list.push(track);
      byLane.set(track.familyId, list);
    }
    return byLane;
  }, [tracks]);

  const hoveredTrack = hoveredId
    ? tracks.find((track) => track.id === hoveredId)
    : undefined;
  const hoveredLane = hoveredTrack
    ? laneById.get(hoveredTrack.familyId)
    : undefined;
  const contentHeight = plotBottom + PADDING_BOTTOM;
  const scrolling = isPlotSizeValid && contentHeight > height + 0.5;

  const highlightIds = useMemo(
    () => new Set(highlight?.ids ?? []),
    [highlight]
  );
  const highlightLeftIds = useMemo(
    () => new Set(highlight?.leftIds ?? []),
    [highlight]
  );
  const highlighting = highlight !== null && hoveredTrack === undefined;
  const isHighlighted = useCallback(
    (id: string) => highlightIds.has(id) || highlightLeftIds.has(id),
    [highlightIds, highlightLeftIds]
  );
  const litLanes = useMemo((): Set<string> | null => {
    if (hoveredTrack) return new Set([hoveredTrack.familyId]);
    if (!highlighting) return null;
    const lit = new Set<string>();
    for (const track of tracks) {
      if (
        isHighlighted(track.id) ||
        track.folded.some((company) => isHighlighted(company.id))
      ) {
        lit.add(track.familyId);
      }
    }
    return lit;
  }, [hoveredTrack, highlighting, tracks, isHighlighted]);
  const branchHosts = useMemo((): Set<string> => {
    const hosts = new Set<string>();
    if (!highlighting) return hosts;
    for (const track of tracks) {
      if (track.kind !== 'track' || track.groupSpan === null) continue;
      if (
        track.folded.some(
          (company) =>
            (highlightIds.has(company.id) && company.y !== company.ownY) ||
            (highlightLeftIds.has(company.id) &&
              company.leftY !== null &&
              company.leftY !== company.ownY)
        )
      ) {
        hosts.add(track.id);
      }
    }
    return hosts;
  }, [highlighting, tracks, highlightIds, highlightLeftIds]);
  const isTrackDimmed = useCallback(
    (track: TimelineTrack) =>
      highlighting && !isHighlighted(track.id) && !branchHosts.has(track.id),
    [highlighting, isHighlighted, branchHosts]
  );

  const trackByRow = useMemo(
    () => new Map(tracks.map((track) => [track.y, track])),
    [tracks]
  );
  const { restingByLane, busiest } = useMemo(() => {
    const pointsByLane = lanes.map((lane) => ({
      id: lane.id,
      points: (tracksByLane.get(lane.id) ?? []).flatMap((track) =>
        track.folded.map(atJoin)
      ),
    }));
    const estimate = busiestOf(
      pointsByLane.map(({ points }) => toStations(points, 1))
    );
    const byLane = new Map(
      pointsByLane.map(({ id, points }) => [id, toStations(points, estimate)])
    );
    return {
      restingByLane: byLane,
      busiest: Math.max(estimate, busiestOf([...byLane.values()])),
    };
  }, [lanes, tracksByLane]);
  const hoveredStations = useMemo((): Station[] => {
    if (hoveredStation) return [hoveredStation];
    return hoveredTrack
      ? toStations(hoveredTrack.folded.map(onOwnRow), busiest)
      : [];
  }, [hoveredTrack, hoveredStation, busiest]);
  const activeStation = useMemo((): Station | null => {
    if (hoveredStation) return hoveredStation;
    if (!hoveredTrack || pointerX === null) return null;
    let nearest: Station | null = null;
    for (const station of hoveredStations) {
      if (station.y !== hoveredTrack.y) continue;
      const distance = Math.abs(station.x - pointerX);
      if (
        distance <= radiusOf(station, busiest) + STATION_HIT_SLOP &&
        (nearest === null || distance < Math.abs(nearest.x - pointerX))
      ) {
        nearest = station;
      }
    }
    return nearest;
  }, [hoveredStation, hoveredStations, hoveredTrack, pointerX, busiest]);

  const obstaclesByTrack = useMemo(
    (): Box[][] =>
      tracks.map((track) => {
        const half = track.runWidth / 2;
        const run: Box = {
          x0: track.startX,
          x1: track.endX,
          y0: track.y - half - OBSTACLE_PADDING,
          y1: track.y + half + OBSTACLE_PADDING,
        };
        if (!track.elbowEnd) return [run];
        const { x: toX, y: toY } = track.elbowEnd;
        const steps = Math.max(
          Math.ceil(Math.hypot(toX - track.endX, toY - track.y) / half),
          1
        );
        const slant = Array.from({ length: steps + 1 }, (_, index) => {
          const cx = track.endX + ((toX - track.endX) * index) / steps;
          const cy = track.y + ((toY - track.y) * index) / steps;
          return {
            x0: cx - half - OBSTACLE_PADDING,
            x1: cx + half + OBSTACLE_PADDING,
            y0: cy - half - OBSTACLE_PADDING,
            y1: cy + half + OBSTACLE_PADDING,
          };
        });
        return [run, ...slant];
      }),
    [tracks]
  );

  const labelFontSize = Math.min(
    TRACK_LABEL_FONT_SIZE,
    Math.max(pitch * LABEL_FONT_RATIO, LABEL_MIN_FONT_SIZE)
  );

  const labelsByLane = useMemo((): Map<string, LabelPlacement[]> => {
    const byLane = new Map<string, LabelPlacement[]>();
    const everyRow = pitch >= LABEL_EVERY_ROW_PITCH;
    const obstacles: Box[] = [
      ...obstaclesByTrack.flat(),
      ...tracks.flatMap((track) =>
        track.exitLink ? exitLinkBoxes(track.exitLink) : []
      ),
    ];
    const placed: Box[] = [];

    for (const track of tracks) {
      if (track.kind !== 'track') continue;
      if (!everyRow && track.share < LABEL_MIN_SHARE) continue;
      const lane = laneById.get(track.familyId);
      if (!lane) continue;
      const fontSize = labelFontSize;
      const inLane = (box: Box): boolean =>
        box.y0 >= lane.y0 && box.y1 <= lane.y1;
      const isFree = (box: Box): boolean =>
        inLane(box) &&
        !obstacles.some((other) => overlaps(box, other)) &&
        !placed.some((other) => overlaps(box, other));
      const measure = (text: string): number => measureText(text, fontSize);
      const readable = (text: string): boolean => isReadable(text, track.label);
      const leftOfStart = (): LabelPlacement | null => {
        const x1 = track.startX - LABEL_CLEARANCE;
        const y0 = track.y - fontSize / 2;
        const y1 = track.y + fontSize / 2;
        let left = PADDING_LEFT;
        for (const other of [...obstacles, ...placed]) {
          if (other.y0 >= y1 || other.y1 <= y0 || other.x0 >= x1) continue;
          if (other.x1 > x1) return null;
          left = Math.max(left, other.x1 + LABEL_CLEARANCE);
        }
        if (x1 - left <= 0) return null;
        const text = truncateText({
          text: track.label,
          maxWidth: x1 - left,
          fontSize,
        });
        if (!readable(text)) return null;
        const textWidth = measure(text);
        placed.push({ x0: x1 - textWidth, x1, y0, y1 });
        return {
          track,
          text,
          x: x1 - textWidth,
          y: track.y + fontSize / 2 - 1.5,
          fontSize,
        };
      };
      const besideRun = (side: 'above' | 'below'): LabelPlacement | null => {
        const from = Math.max(track.startX, plotLeft) + 3;
        const text = truncateText({
          text: track.label,
          maxWidth: track.endX - from - 3,
          fontSize,
        });
        if (!readable(text)) return null;
        const textWidth = measure(text);
        const half = track.runWidth / 2;
        const y0 =
          side === 'above'
            ? track.y - half - LABEL_CLEARANCE / 2 - fontSize
            : track.y + half + LABEL_CLEARANCE / 2;
        const y1 = y0 + fontSize;
        const maxX = track.endX - textWidth - 3;
        const step = Math.max((maxX - from) / LABEL_SHIFT_STEPS, 1);
        for (let x = from; x <= maxX + 0.01; x += step) {
          const box: Box = { x0: x, x1: x + textWidth, y0, y1 };
          if (!isFree(box)) continue;
          placed.push(box);
          return {
            track,
            text,
            x,
            y: side === 'above' ? y1 - 2 : y1 - 1.5,
            fontSize,
          };
        }
        return null;
      };
      const placement =
        leftOfStart() ?? besideRun('above') ?? besideRun('below');
      if (!placement) continue;
      const list = byLane.get(lane.id) ?? [];
      list.push(placement);
      byLane.set(lane.id, list);
    }
    return byLane;
  }, [tracks, pitch, plotLeft, laneById, obstaclesByTrack, labelFontSize]);

  const familyNameX = useCallback(
    (lane: Lane): number =>
      (tracks.find((track) => track.id === lane.id)?.endX ?? plotRight) +
      NAME_GAP,
    [tracks, plotRight]
  );

  // A dated exit repeats the company's name where its link reaches the row,
  // with how it left after it.
  const exitLabelsByLane = useMemo((): Map<string, ExitPlacement[]> => {
    const fontSize = labelFontSize;
    const taken: Box[] = [...labelsByLane.values()].flat().map(labelBox);
    if (nameColumnWidth > 0) {
      for (const lane of lanes) {
        const x0 = familyNameX(lane);
        taken.push({
          x0,
          x1: x0 + measureText(lane.label, FAMILY_NAME_FONT_SIZE),
          y0: lane.y - FAMILY_NAME_FONT_SIZE / 2,
          y1: lane.y + FAMILY_NAME_FONT_SIZE / 2,
        });
      }
    }
    // Drops into a parent and exit links may cross the words, which wear a
    // halo; straight runs and other words may not.
    const blockers: Box[] = [
      ...obstaclesByTrack.flatMap((boxes) => boxes.slice(0, 1)),
      ...tracks.flatMap((track) =>
        track.exitLink ? exitLinkBoxes(track.exitLink).slice(1) : []
      ),
    ];
    const isFree = (box: Box): boolean =>
      !blockers.some((other) => overlaps(box, other)) &&
      !taken.some((other) => overlaps(box, other));
    const right = width - PADDING_RIGHT;
    const byLane = new Map<string, ExitPlacement[]>();
    for (const track of tracks) {
      if (!track.exitLink) continue;
      const x = track.exitLink.x + EXIT_LINK_STUB + EXIT_LINK_LABEL_GAP;
      const text = truncateText({
        text: track.label,
        maxWidth: right - x,
        fontSize,
      });
      if (!isReadable(text, track.label)) continue;
      const placement: LabelPlacement = {
        track,
        text,
        x,
        y: track.y + fontSize / 2 - 1.5,
        fontSize,
      };
      const box = labelBox(placement);
      if (!isFree(box)) continue;
      taken.push(box);
      const words = exitLabel(track.exitKind, track.exitTo);
      let outcome: string | null = null;
      if (words !== null && text === track.label) {
        const outcomeX = box.x1 + EXIT_LINK_LABEL_GAP;
        const fitted = truncateText({
          text: words,
          maxWidth: right - outcomeX,
          fontSize: EXIT_LABEL_FONT_SIZE,
        });
        const outcomeBox: Box = {
          ...box,
          x0: outcomeX,
          x1: outcomeX + measureText(fitted, EXIT_LABEL_FONT_SIZE),
        };
        if (isReadable(fitted, words) && isFree(outcomeBox)) {
          outcome = fitted;
          taken.push(outcomeBox);
        }
      }
      const list = byLane.get(track.familyId) ?? [];
      list.push({ ...placement, outcome });
      byLane.set(track.familyId, list);
    }
    return byLane;
  }, [
    tracks,
    lanes,
    width,
    nameColumnWidth,
    labelsByLane,
    obstaclesByTrack,
    labelFontSize,
    familyNameX,
  ]);

  // A direct subsidiary that left has no parent row to link out of: its run
  // just stops, so the outcome follows the lane's name instead.
  const familyExits = useMemo((): Map<string, ExitPlacement> => {
    const byLane = new Map<string, ExitPlacement>();
    for (const lane of lanes) {
      const track = tracks.find((candidate) => candidate.id === lane.id);
      if (!track || track.kind !== 'family' || track.endTime >= domain[1]) {
        continue;
      }
      const x = familyNameX(lane);
      const text = truncateText({
        text: lane.label,
        maxWidth: width - x - PADDING_RIGHT,
        fontSize: FAMILY_NAME_FONT_SIZE,
      });
      const words = exitLabel(track.exitKind, track.exitTo);
      let outcome: string | null = null;
      if (nameColumnWidth > 0 && words !== null && text === lane.label) {
        const outcomeX =
          x + measureText(text, FAMILY_NAME_FONT_SIZE) + EXIT_LINK_LABEL_GAP;
        const fitted = truncateText({
          text: words,
          maxWidth: width - PADDING_RIGHT - outcomeX,
          fontSize: EXIT_LABEL_FONT_SIZE,
        });
        if (isReadable(fitted, words)) outcome = fitted;
      }
      byLane.set(lane.id, {
        track,
        text,
        x,
        y: lane.y,
        fontSize: FAMILY_NAME_FONT_SIZE,
        outcome,
      });
    }
    return byLane;
  }, [lanes, tracks, domain, width, nameColumnWidth, familyNameX]);

  // The buyer or outcome goes in the tooltip when the chart cannot show it whole.
  const tooltipExitNote = useMemo((): string | null => {
    if (!hoveredTrack) return null;
    const words = exitLabel(hoveredTrack.exitKind, hoveredTrack.exitTo);
    if (words === null) return null;
    const placed =
      familyExits.get(hoveredTrack.id) ??
      (exitLabelsByLane.get(hoveredTrack.familyId) ?? []).find(
        (placement) => placement.track.id === hoveredTrack.id
      );
    return placed?.outcome === words ? null : words;
  }, [hoveredTrack, familyExits, exitLabelsByLane]);

  const decadeTicks = useMemo(
    () =>
      ticks.filter(
        (tick: TimelineTick) => Number(tick.label) % DECADE_GRID_YEARS === 0
      ),
    [ticks]
  );

  const xOfTime = useCallback(
    (time: number): number => {
      const [start, end] = domain;
      if (end <= start) return plotLeft;
      const ratio = Math.min(Math.max((time - start) / (end - start), 0), 1);
      return plotLeft + ratio * (plotRight - plotLeft);
    },
    [domain, plotLeft, plotRight]
  );

  const highlightBand = useMemo(() => {
    if (!highlighting || highlight === null) return null;
    const timeOf = (iso: string): number =>
      Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00Z` : iso);
    const from = timeOf(highlight.from);
    const to = timeOf(highlight.to);
    if (Number.isNaN(from) || Number.isNaN(to)) return null;
    const x0 = xOfTime(from);
    const x1 = xOfTime(to);
    return x1 - x0 > 0 ? { x0, x1 } : null;
  }, [highlighting, highlight, xOfTime]);

  const joinMarkerX = useMemo(() => {
    if (activeStation) return activeStation.x;
    if (!hoveredTrack || hoveredTrack.joinTime === null) return null;
    if (hoveredTrack.joinTime <= domain[0]) return null;
    return xOfTime(hoveredTrack.joinTime);
  }, [activeStation, hoveredTrack, domain, xOfTime]);

  const exitMarker = useMemo(() => {
    if (activeStation || !hoveredTrack) return null;
    if (hoveredTrack.endTime >= domain[1]) return null;
    return {
      x: xOfTime(hoveredTrack.endTime),
      y: hoveredTrack.exitLink?.y1 ?? hoveredTrack.y,
    };
  }, [activeStation, hoveredTrack, domain, xOfTime]);

  const foregroundGroups = ((): ForegroundGroup[] => {
    const groups: ForegroundGroup[] = [];
    if (hoveredTrack && hoveredLane) {
      const span = hoveredTrack.groupSpan;
      const showsBranch =
        hoveredTrack.kind === 'track' &&
        span !== null &&
        hoveredTrack.folded.some((company) => company.x > span.x0 + 1);
      groups.push({
        lane: hoveredLane,
        stations: hoveredStations,
        spans: showsBranch ? [{ track: hoveredTrack, left: false }] : [],
      });
    } else if (highlighting) {
      for (const lane of lanes) {
        const laneTracks = tracksByLane.get(lane.id) ?? [];
        const joined = laneTracks.flatMap((track) =>
          track.folded
            .filter((company) => highlightIds.has(company.id))
            .map(branchHosts.has(track.id) ? onOwnRow : atJoin)
        );
        const departed = laneTracks.flatMap((track) => {
          const onBranch = branchHosts.has(track.id);
          return track.folded
            .filter((company) => highlightLeftIds.has(company.id))
            .flatMap((company) =>
              atExit(company).map((point) =>
                onBranch ? { ...point, y: company.ownY } : point
              )
            );
        });
        const stations = [
          ...toStations(joined, busiest),
          ...toStations(departed, busiest, true),
        ];
        const spans = [
          ...laneTracks
            .filter((track) => branchHosts.has(track.id))
            .map((track) => ({ track, left: false })),
          ...laneTracks
            .filter((track) => highlightLeftIds.has(track.id))
            .map((track) => ({ track, left: true })),
        ];
        if (stations.length > 0 || spans.length > 0) {
          groups.push({ lane, stations, spans });
        }
      }
    }
    return groups;
  })();
  const foregroundStations = foregroundGroups.flatMap(
    (group) => group.stations
  );

  const renderStation = (
    station: Station,
    className: string | undefined,
    key: string
  ) => (
    <circle
      key={key}
      cx={station.x}
      cy={station.y}
      r={radiusOf(station, busiest)}
      className={classNames(
        styles.station,
        station.left && styles.stationLeft,
        className
      )}
      data-testid="timeline-chart__station"
    />
  );

  const renderExit = (track: TimelineTrack, link: ExitLink) => (
    <>
      <path
        d={exitLinkPath(link)}
        strokeWidth={track.elbowWidth || track.runWidth}
        data-testid="timeline-chart__exit-link"
        {...interactive(track.id, styles.elbow)}
      />
      {track.exitKind === 'dissolved' && (
        <line
          x1={link.x}
          x2={link.x}
          y1={link.y0 - EXIT_BAR_HALF}
          y2={link.y0 + EXIT_BAR_HALF}
          className={styles.exitBar}
          data-testid="timeline-chart__exit-dissolved"
        />
      )}
    </>
  );

  const renderLane = (lane: Lane) => {
    const dimmed = litLanes !== null && !litLanes.has(lane.id);
    const laneTracks = tracksByLane.get(lane.id) ?? [];
    const nameX = familyNameX(lane);
    const familyExit = familyExits.get(lane.id);
    return (
      <g
        key={lane.id}
        className={classNames(styles.lane, dimmed && styles.laneDimmed)}
        style={laneVars(lane)}
        data-testid="timeline-chart__lane"
      >
        {laneTracks.map((track) => (
          <g
            key={track.id}
            className={classNames(isTrackDimmed(track) && styles.trackDimmed)}
            data-testid="timeline-chart__track"
          >
            <path d={track.path} {...interactive(track.id, styles.track)} />
            {track.elbowPath && (
              <path
                d={track.elbowPath}
                strokeWidth={track.elbowWidth}
                {...interactive(track.id, styles.elbow)}
              />
            )}
            {track.exitLink && renderExit(track, track.exitLink)}
          </g>
        ))}
        {(restingByLane.get(lane.id) ?? [])
          .filter(
            (station) =>
              !foregroundStations.some((shown) =>
                stationsOverlap(station, shown, busiest)
              )
          )
          .map((station) =>
            renderStation(
              station,
              undefined,
              `resting-${station.x}-${station.y}`
            )
          )}
        {(labelsByLane.get(lane.id) ?? []).map(
          ({ track, text, x, y, fontSize }) => (
            <text
              key={track.id}
              x={x}
              y={y}
              fontSize={fontSize}
              data-testid="timeline-chart__label"
              {...interactive(
                track.id,
                classNames(
                  styles.trackLabel,
                  isTrackDimmed(track) && styles.trackDimmed
                )
              )}
            >
              {text}
            </text>
          )
        )}
        {(exitLabelsByLane.get(lane.id) ?? []).map(
          ({ track, text, x, y, fontSize, outcome }) => (
            <text
              key={`${track.id}-exit`}
              x={x}
              y={y}
              fontSize={fontSize}
              data-testid="timeline-chart__exit-name"
              {...interactive(
                track.id,
                classNames(
                  styles.trackLabel,
                  styles.exitName,
                  isTrackDimmed(track) && styles.trackDimmed
                )
              )}
            >
              {text}
              {outcome !== null && hoveredTrack?.id === track.id && (
                <tspan
                  dx={EXIT_LINK_LABEL_GAP}
                  fontSize={EXIT_LABEL_FONT_SIZE}
                  className={styles.exitLabel}
                  data-testid="timeline-chart__exit-label"
                >
                  {outcome}
                </tspan>
              )}
            </text>
          )
        )}
        {nameColumnWidth > 0 && (
          <text
            x={nameX}
            y={lane.y}
            dominantBaseline="central"
            fontSize={FAMILY_NAME_FONT_SIZE}
            data-testid="timeline-chart__family-name"
            {...interactive(lane.id, styles.familyName)}
          >
            {truncateText({
              text: lane.label,
              maxWidth: width - nameX - PADDING_RIGHT,
              fontSize: FAMILY_NAME_FONT_SIZE,
            })}
            {familyExit?.outcome != null && hoveredTrack?.id === lane.id && (
              <tspan
                dx={EXIT_LINK_LABEL_GAP}
                fontSize={EXIT_LABEL_FONT_SIZE}
                className={styles.exitLabel}
                data-testid="timeline-chart__exit-label"
              >
                {familyExit.outcome}
              </tspan>
            )}
          </text>
        )}
        {familyExit?.track.exitKind === 'dissolved' && (
          <line
            x1={familyExit.track.endX}
            x2={familyExit.track.endX}
            y1={
              lane.y -
              Math.max(EXIT_BAR_HALF, familyExit.track.runWidth / 2 + 2)
            }
            y2={
              lane.y +
              Math.max(EXIT_BAR_HALF, familyExit.track.runWidth / 2 + 2)
            }
            className={classNames(
              styles.exitBar,
              isTrackDimmed(familyExit.track) && styles.trackDimmed
            )}
            data-testid="timeline-chart__exit-dissolved"
          />
        )}
      </g>
    );
  };

  const renderForeground = () => {
    const groups = foregroundGroups;
    if (groups.length === 0) return null;
    return (
      <g className={styles.foreground} data-testid="timeline-chart__foreground">
        {groups.map(({ lane, stations, spans }) => (
          <g key={lane.id} style={laneVars(lane)}>
            {spans.map(({ track, left }) =>
              track.groupSpan ? (
                <line
                  key={`${track.id}-span`}
                  x1={track.groupSpan.x0}
                  x2={track.groupSpan.x1}
                  y1={track.y}
                  y2={track.y}
                  strokeWidth={track.runWidth}
                  className={classNames(styles.span, left && styles.leftSpan)}
                  data-testid={
                    left
                      ? 'timeline-chart__left-span'
                      : 'timeline-chart__branch-span'
                  }
                />
              ) : null
            )}
            {stations.map((station) =>
              renderStation(
                station,
                (!hoveredTrack && !station.left) || station === activeStation
                  ? styles.stationActive
                  : undefined,
                `${station.left ? 'left' : 'joined'}-${station.x}-${station.y}`
              )
            )}
          </g>
        ))}
      </g>
    );
  };

  const axisLayer = (
    <g id="x-axis" data-testid="plot-x-axis">
      <line
        x1={plotLeft}
        x2={plotRight}
        y1={plotBottom + 0.5}
        y2={plotBottom + 0.5}
        className={styles.axisLine}
      />
      {ticks
        .filter((tick: TimelineTick) => tick.major)
        .map((tick) => (
          <text
            key={tick.label}
            x={tick.x}
            y={plotBottom + AXIS_FONT_SIZE + 6}
            textAnchor="middle"
            fontSize={AXIS_FONT_SIZE}
            className={styles.axisLabel}
          >
            {tick.label}
          </text>
        ))}
    </g>
  );

  const plotLayers = (
    <>
      <g id="plot-background">
        {highlightBand && (
          <rect
            x={highlightBand.x0}
            y={plotTop}
            width={highlightBand.x1 - highlightBand.x0}
            height={plotBottom - plotTop}
            className={styles.highlightBand}
            data-testid="timeline-chart__highlight-band"
          />
        )}
        {decadeTicks.map((tick) => (
          <line
            key={tick.label}
            x1={tick.x}
            x2={tick.x}
            y1={plotTop}
            y2={plotBottom}
            className={styles.gridLine}
          />
        ))}
      </g>
      <g id="lanes">{lanes.map(renderLane)}</g>
      {renderForeground()}
      <g data-testid="timeline-chart__station-hits">
        {lanes.flatMap((lane) =>
          (restingByLane.get(lane.id) ?? []).flatMap((station) => {
            const owner = trackByRow.get(station.y);
            if (!owner) return [];
            const handlers = interactive(owner.id, styles.stationHit);
            return [
              <circle
                key={`hit-${station.x}-${station.y}`}
                cx={station.x}
                cy={station.y}
                r={radiusOf(station, busiest) + STATION_HIT_SLOP}
                data-testid="timeline-chart__station-hit"
                {...handlers}
                onMouseEnter={() => {
                  handlers.onMouseEnter();
                  setHoveredStation(station);
                }}
              />,
            ];
          })
        )}
      </g>
      {joinMarkerX !== null && (
        <line
          x1={joinMarkerX}
          x2={joinMarkerX}
          y1={
            activeStation?.y ??
            hoveredTrack?.elbowEnd?.y ??
            hoveredTrack?.y ??
            plotBottom
          }
          y2={plotBottom}
          className={styles.joinMarker}
          data-testid="timeline-chart__join-marker"
        />
      )}
      {exitMarker !== null && (
        <line
          x1={exitMarker.x}
          x2={exitMarker.x}
          y1={exitMarker.y}
          y2={plotBottom}
          className={styles.joinMarker}
          data-testid="timeline-chart__exit-marker"
        />
      )}
    </>
  );

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, className)}
      data-testid={plotTestId(ReplotChartNames.TimelineChart)}
    >
      <PlotLoadWrapper loading={loading} noData={!hasData}>
        {scrolling ? (
          <div className={styles.scrollFrame}>
            <div
              className={styles.scrollBody}
              data-testid="timeline-chart__scroll"
            >
              <svg ref={plotRef} width={width} height={plotBottom}>
                <g id="render-container">{plotLayers}</g>
              </svg>
            </div>
            <svg
              className={styles.axisStrip}
              width="100%"
              height={PADDING_BOTTOM}
            >
              <g transform={`translate(0 ${-plotBottom})`}>{axisLayer}</g>
            </svg>
          </div>
        ) : (
          <svg ref={plotRef} width="100%" height="100%">
            {isPlotSizeValid && (
              <g id="render-container">
                {axisLayer}
                {plotLayers}
              </g>
            )}
          </svg>
        )}
        {hoveredTrack && cursor && (
          <PlotTooltip
            isVisible
            x={cursor.x}
            y={cursor.y}
            placement="vertical"
            space="viewport"
          >
            <TimelineTooltipContent
              track={hoveredTrack}
              domainEnd={domain[1]}
              format={format}
              station={activeStation?.companies ?? null}
              exitNote={tooltipExitNote}
            />
          </PlotTooltip>
        )}
      </PlotLoadWrapper>
    </div>
  );
};
